import Model from '~/models/base/Model';
import Entity from '~/models/Entity';
import ActionResult from '~/models/ActionResult';

export const objectTypes = Object.freeze([
    {
        human: 'File',
        dashed: 'artifacts',
        internal: ['md5', 'sha1', 'sha256', 'file', 'HASH', 'FILE']
    },
    {
        human: 'IP',
        dashed: 'ip-addresses',
        internal: ['ipaddress', 'IP']
    },
    {
        human: 'URL',
        dashed: 'url-addresses',
        internal: ['url', 'URL']
    },
    {
        human: 'Email',
        dashed: 'mail-addresses',
        internal: ['email', 'EMAIL', 'Email']
    },
    {
        human: 'User details',
        dashed: 'user-details',
        internal: ['userdetail', 'USER_DETAILS', 'USER DETAILS', 'User details']
    },
    {
        human: 'Domain',
        dashed: 'domain',
        internal: ['domain', 'DOMAIN', 'Domain']
    },
    {
        human: 'Hash',
        dashed: 'hash',
        internal: ['hash', 'HASH', 'Hash']
    },
]);

/**
 * class representing Object
 * @extends Model
 */
export default class ObjectModel extends Model {
    static _url = `${Model._url}/objects/`;

    get isArtifact() {
        return this.type?.startsWith?.('Artifact');
    }

    get isFile() {
        return 'file' === this.type?.toLowerCase();
    }

    static all(params) {
        const { incidentId, ...payload } = params || {};
        payload.url = incidentId
            ? `${Model._url}/incidents/${incidentId}/objects/`
            : this._url;
        return Model.all(payload);
    }

    static getObjectResult(incidentId, objectId, resultId) {
        const requestUrl = `${Model._url}/incidents/${incidentId}/objects/${objectId}/results/${resultId}/`;
        const requestPayload = {
            distinct: 0,
            start: 0,
            step: 1
        };
        return this.api.post(requestUrl, requestPayload);
    }

    static _findTypes(value) {
        return objectTypes.find(objectType =>
            objectType.internal.includes(value)
        );
    }

    static _getDashedType(humanType) {
        return this._findTypes(humanType)?.dashed;
    }

    static _getInternalType(humanType) {
        return this._findTypes(humanType)?.internal;
    }

    static _getIntegerType(humanType) {
        return this._findTypes(humanType);
    }

    static _toBaseUrl(params) {
        const id = params?.incident_id;
        return `${Model._url}/${id ? `incidents/${id}/objects` : 'entities'}`;
    }

    static markFavorite(params) {
        const requestUrl = `${this._toBaseUrl(params)}/favorite/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    static unmarkFavorite(params) {
        const requestUrl = `${this._toBaseUrl(params)}/unfavorite/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    static lock(params) {
        const requestUrl = `${this._toBaseUrl(params)}/lock/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    static unlock(params) {
        const requestUrl = `${this._toBaseUrl(params)}/unlock/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    static delete(params) {
        const incidentId = params?.incident_id;
        if (incidentId) {
            const requestUrl = `${Model._url}/incidents/${incidentId}/objects/`;
            const requestPayload = { data: params }; // <<
            return this.api.delete(requestUrl, requestPayload);
        } else {
            const requestUrl = `${Model._url}/entities/delete/`;
            const requestPayload = params ?? {};
            return this.api.patch(requestUrl, requestPayload);
        }
    }

    static restore(params) {
        const requestPayload = params || {};
        const incidentId = requestPayload.incident_id;
        if (incidentId) {
            const requestUrl = `${Model._url}/incidents/${incidentId}/objectsrestore/`;
            return this.api.post(requestUrl, requestPayload);
        } else {
            const requestUrl = `${Model._url}/entities/restore/`;
            return this.api.patch(requestUrl, requestPayload);
        }
    }

    static download(id) {
        const requestUrl = `${Model._url}/entities/file/${id}/download/`;
        const requestPayload = { responseType: 'blob' };
        return this.api.get(requestUrl, requestPayload);
    }

    static executeBulkActions(params, configurations) {
        const requestUrl = `${APP_URL.automation}/executeObservableAction/bulk/`;
        const action = params || {};
        const requestPayload = {
            action_id: action.id,
            action_internal_name: action.internalName,
            type: action.type,
            asset_id: action.resourceId,
            configurations: configurations
        };
        return this.api.post(requestUrl, requestPayload);
    }

    static reExecuteAction(params) {
        const requestUrl = `${APP_URL.automation}/executeObservableAction/`;
        const requestPayload = params || {};
        requestPayload.op_type = 're_execute_action';
        return this.api.post(requestUrl, requestPayload);
    }

    static kill(params) {
        const requestUrl = `${APP_URL.automation}/objects/${params.objectId}/result/${params.resultId}/kill/`;
        return this.api.patch(requestUrl);
    }

    static saveObservable(params) {
        const requestUrl = `${APP_URL.api}/entities/`;
        const requestPayload = params || {};
        requestPayload.op_type = 'edit_action_source';
        return this.api.post(requestUrl, requestPayload);
    }

    static checkEntityExist(entityType, params) {
        const requestUrl = `${APP_URL.api}/entities/contextualmenu/${entityType}/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    static retrieveAvailableActionsByType(entityType) {
        const requestUrl = `${Model._url}/entities/contextualmenu/${entityType}/actions/`;
        return this.api.get(requestUrl);
    }

    static validateValue(entityType, value) {
        const requestUrl = `${APP_URL.api}/entities/contextualmenu/${entityType}/validate/`;
        const requestPayload = { value: value };
        return this.api.post(requestUrl, requestPayload);
    }

    getInternalType() {
        return this.constructor._getInternalType(this.type);
    }

    getDashedType() {
        return this.constructor._getDashedType(this.type);
    }

    getIntegerType() {
        return this.constructor._getIntegerType(this.type);
    }

    getLatestResults() {
        const originalType = this.type;
        const isFileType = 'file' === originalType?.toLowerCase();
        const type =  isFileType ? originalType : this.constructor._getDashedType(originalType);
        // console.log({ instance: this, type, isFileType, originalType });
        return type
            ? ActionResult.getLatestByType(this.id, this.incidentId, type)
            : Promise.reject({ type, originalType });
    }

    getResultList(start, step) {
        return ActionResult.getResultList(
            this.id,
            this.incidentId,
            start,
            step
        );
    }

    getActions() {
        const requestUrl = `${APP_URL.api}/objects/${this.id}/actions/`;
        return new Promise(resolve => {
            this.constructor.api.get(requestUrl).then(response => {
                const respData = response.data;
                const actions = {};
                for (const type of [
                    'Enrichment',
                    'Containment',
                    'Custom',
                    'Notification',
                    'Scheduled'
                ]) {
                    actions[type] = respData[type]?.reduce((acc, current) => {
                        const currentNameSTR = current.name
                            .trim()
                            .toUpperCase();
                        const foundIndex = acc.findIndex(item => {
                            // gb: Removed because we can't anymore having actions with same internal_name
                            // item.internalName === current.internalName &&
                            const nameStr = item.name.trim();
                            return (
                                currentNameSTR === nameStr ||
                                currentNameSTR === nameStr.toUpperCase()
                            );
                        });
                        if (-1 !== foundIndex) {
                            const name = current.internalName;
                            for (const resource of current.resources) {
                                resource.actionInternalName = name;
                            }
                            const found = acc[foundIndex];
                            // note: may be a concat here?
                            found.resources = [
                                ...found.resources,
                                ...current.resources
                            ];
                            return acc;
                        } else {
                            return acc.concat([current]);
                        }
                    }, []);
                }
                resolve({
                    data: actions
                });
            });
        });
    }

    executeAction(action) {
        const { id, incidentId } = this;
        const params = action || {};
        const requestPayload = {
            resource_id: id,
            action_id: params.id,
            asset_id: params.resourceId,
            action_internal_name: params.internalName,
            configuration_values: JSON.stringify(params.configurationValues)
        };
        if (incidentId) {
            requestPayload.incident_id = incidentId;
        }
        const paramResourceValue = params.resource_value;
        if (paramResourceValue) {
            const paramType = params.type;
            if (paramType) {
                requestPayload.type = paramType;
                requestPayload.resource_value = paramResourceValue;
            }
        }
        const requestUrl = `${APP_URL.automation}/executeObservableAction/`;
        return this.constructor.api.post(requestUrl, requestPayload);
    }

    save(params) {
        const { id, incidentId } = this;
        if (id) {
            const requestUrl = `${APP_URL.api}/${
                incidentId
                    ? `incidents/${incidentId}/objects/${id}/`
                    : `entities/${id}/`
            }`;
            const requestPayload = params ?? {};
            return this.constructor.api.put(requestUrl, requestPayload);
        } else {
            if (APP_INFO.debug) {
                console.trace('✘ saving:%O', this);
            }
        }
    }

    get(params) {
        const { id, incidentId } = this;
        if (incidentId) {
            const requestUrl = `${APP_URL.api}/incidents/${incidentId}/objects/${id}/`;
            const requestPayload = { params: params ?? {} };
            return this.constructor.api.get(requestUrl, requestPayload);
        } else {
            return Entity.get(id);
        }
    }
}
